// Chamada - Registro de Presença
import { useState, useEffect } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, FileText, UserX, Download } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";


export default function Chamada({ onLogout }) {
  const [turmas, setTurmas] = useState([]);
  const [alunos, setAlunos] = useState([]);
  const [selectedTurma, setSelectedTurma] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [presencas, setPresencas] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingPresencas, setLoadingPresencas] = useState(false);
  
  // Relatório do dia
  const [showRelatorio, setShowRelatorio] = useState(false);
  const [relatorioDia, setRelatorioDia] = useState(null);
  const [loadingRelatorio, setLoadingRelatorio] = useState(false);

  useEffect(() => {
    fetchTurmas();
  }, []);

  useEffect(() => {
    if (selectedTurma) {
      fetchAlunosETurma();
    }
  }, [selectedTurma, selectedDate]);

  const fetchTurmas = async () => {
    try {
      const response = await api.get('/turmas');
      // Ordenar turmas alfabeticamente
      const turmasOrdenadas = response.data.sort((a, b) => 
        a.nome.localeCompare(b.nome, 'pt-BR')
      );
      setTurmas(turmasOrdenadas);
    } catch (error) {
      toast.error("Erro ao carregar turmas");
    }
  };

  const fetchAlunosETurma = async () => {
    setLoadingPresencas(true);
    try {
      
      const [alunosRes, presencasRes] = await Promise.all([
        api.get('/alunos'),
        api.get(`/presencas/turma/${selectedTurma}?data=${format(selectedDate, 'yyyy-MM-dd')}`)
      ]);

      const alunosDaTurma = alunosRes.data.filter(a => {
        // Suporte a migração de dados antigos
        const turmaIds = a.turma_ids || (a.turma_id ? [a.turma_id] : []);
        return turmaIds.includes(selectedTurma);
      });
      
      // Ordenar alunos alfabeticamente
      const alunosOrdenados = alunosDaTurma.sort((a, b) => 
        a.nome.localeCompare(b.nome, 'pt-BR')
      );
      setAlunos(alunosOrdenados);

      // Configurar presenças existentes
      const presencasMap = {};
      presencasRes.data.forEach(p => {
        presencasMap[p.aluno_id] = p.presente;
      });

      // Inicializar todos como presente se não houver registro
      const initialPresencas = {};
      alunosOrdenados.forEach(aluno => {
        initialPresencas[aluno.id] = presencasMap[aluno.id] !== undefined ? presencasMap[aluno.id] : true;
      });
      setPresencas(initialPresencas);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoadingPresencas(false);
    }
  };

  const handleTogglePresenca = (alunoId) => {
    setPresencas(prev => ({
      ...prev,
      [alunoId]: !prev[alunoId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      
      const registros = Object.entries(presencas).map(([aluno_id, presente]) => ({
        aluno_id,
        presente
      }));

      await api.post('/chamada', {
        turma_id: selectedTurma,
        data: format(selectedDate, 'yyyy-MM-dd'),
        registros
      });

      toast.success("Chamada registrada com sucesso!");
      // Limpar tela após salvar: volta para seleção de turma sem mostrar a lista de nomes
      setSelectedTurma("");
      setAlunos([]);
      setPresencas({});
    } catch (error) {
      toast.error("Erro ao registrar chamada");
    } finally {
      setLoading(false);
    }
  };

  // Gerar relatório do dia
  const gerarRelatorioDia = async () => {
    setLoadingRelatorio(true);
    try {
      const response = await api.get(`/relatorios/chamada-dia?data=${format(selectedDate, 'yyyy-MM-dd')}`);
      setRelatorioDia(response.data);
      setShowRelatorio(true);
    } catch (error) {
      toast.error("Erro ao gerar relatório do dia");
    } finally {
      setLoadingRelatorio(false);
    }
  };

  // Contar faltas do dia atual na turma selecionada
  const faltasDoDia = alunos.filter(a => presencas[a.id] === false);

  return (
    <Layout onLogout={onLogout}>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Chamada</h1>
          <p className="text-gray-600">Registre a presença dos alunos</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Selecionar Turma e Data</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Turma</Label>
                <Select value={selectedTurma} onValueChange={setSelectedTurma}>
                  <SelectTrigger data-testid="select-turma">
                    <SelectValue placeholder="Selecione uma turma" />
                  </SelectTrigger>
                  <SelectContent>
                    {turmas.map((turma) => (
                      <SelectItem key={turma.id} value={turma.id}>
                        {turma.nome} - {turma.modalidade}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Data</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      data-testid="select-date"
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(selectedDate, "dd/MM/yyyy", { locale: ptBR })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => date && setSelectedDate(date)}
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            
            {/* Botão para relatório do dia */}
            <div className="mt-4 pt-4 border-t">
              <Button
                variant="outline"
                onClick={gerarRelatorioDia}
                disabled={loadingRelatorio}
                className="w-full md:w-auto"
                data-testid="relatorio-dia-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                {loadingRelatorio ? "Gerando..." : "Relatório de Faltas do Dia"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Relatório do Dia */}
        {showRelatorio && relatorioDia && (
          <Card className="border-orange-200 bg-orange-50">
            <CardHeader className="pb-3">
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2 text-orange-800">
                  <UserX className="w-5 h-5" />
                  Relatório de Faltas - {format(selectedDate, "dd/MM/yyyy", { locale: ptBR })}
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowRelatorio(false)}
                >
                  Fechar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {relatorioDia.turmas_com_faltas.length === 0 ? (
                <p className="text-green-700 font-medium">🎉 Nenhuma falta registrada neste dia!</p>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-orange-700">
                    Total de faltas: <strong>{relatorioDia.total_faltas}</strong> aluno(s)
                  </p>
                  
                  {relatorioDia.turmas_com_faltas.map((turma) => (
                    <div key={turma.turma_id} className="bg-white p-4 rounded-lg border">
                      <h4 className="font-semibold text-gray-800 mb-2">
                        {turma.turma_nome} ({turma.total_faltas} falta{turma.total_faltas > 1 ? 's' : ''})
                      </h4>
                      <div className="space-y-1">
                        {turma.alunos_faltantes
                          .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
                          .map((aluno, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm text-red-600">
                              <UserX className="w-3 h-3" />
                              {aluno.nome}
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {selectedTurma && (
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
                <CardTitle>Lista de Alunos</CardTitle>
                {faltasDoDia.length > 0 && (
                  <span className="text-sm text-red-600 font-medium">
                    {faltasDoDia.length} falta{faltasDoDia.length > 1 ? 's' : ''} marcada{faltasDoDia.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loadingPresencas ? (
                <div className="text-center py-8 text-gray-500">Carregando...</div>
              ) : alunos.length === 0 ? (
                <div className="text-center py-8 text-gray-500" data-testid="no-alunos">Nenhum aluno nesta turma</div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-3">
                    {alunos.map((aluno) => (
                      <div
                        key={aluno.id}
                        className="flex items-center justify-between gap-3 p-3 sm:p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                        data-testid={`aluno-chamada-${aluno.id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <Checkbox
                            data-testid={`checkbox-${aluno.id}`}
                            checked={presencas[aluno.id]}
                            onCheckedChange={() => handleTogglePresenca(aluno.id)}
                            id={`aluno-${aluno.id}`}
                            className="h-5 w-5 shrink-0"
                          />
                          <label
                            htmlFor={`aluno-${aluno.id}`}
                            className="text-sm font-medium cursor-pointer break-words min-w-0"
                          >
                            {aluno.nome}
                          </label>
                        </div>
                        <span className={`text-xs sm:text-sm font-semibold shrink-0 ${
                          presencas[aluno.id] ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {presencas[aluno.id] ? 'Presente' : 'Falta'}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="pt-4 border-t">
                    <Button
                      data-testid="salvar-chamada"
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600"
                    >
                      {loading ? "Salvando..." : "Salvar Chamada"}
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
